import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent, SlideshowSelectEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();

  /** @type {IntersectionObserver | null} */
  #viewportObserver = null;

  /** @type {boolean} */
  #gestureRetryArmed = false;

  /** @type {boolean} */
  #isIntersecting = false;

  /** @type {boolean} */
  #userPaused = false;

  /** @type {boolean} */
  #pausingProgrammatically = false;

  /** @type {AbortController | null} */
  #videoEventsController = null;

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.#onMediaStartedPlaying, { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.#onDialogClose, { signal });

    if (this.hasAttribute('data-viewport-autoplay')) {
      this.#setupViewportAutoplay(signal);
    }

    const existingVideo = this.querySelector('video');
    if (existingVideo instanceof HTMLVideoElement) {
      this.#bindVideoEvents(existingVideo);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#viewportObserver?.disconnect();
    this.#videoEventsController?.abort();
    this.#abortController.abort();
  }

  /**
   * Pause when another media starts, but ignore events from this instance.
   * @param {Event} event
   */
  #onMediaStartedPlaying = (event) => {
    const resource = /** @type {CustomEvent & { detail?: { resource?: HTMLElement } }} */ (event).detail?.resource;
    if (resource === this) return;
    this.pauseMedia();
  };

  /**
   * True when this media lives in a closed or closing dialog (e.g. quick-add).
   * Closed dialog nodes stay in the DOM; HTML5 video keeps playing audio unless we stop it.
   * @returns {boolean}
   */
  #isInsideClosedDialog() {
    const dialog = this.closest('dialog');
    if (!dialog) return false;
    return !dialog.open || dialog.classList.contains('dialog-closing');
  }

  /**
   * Zoom dialog uses DialogCloseEvent both when opening (to pause gallery) and closing.
   * Zoom videos are stopped/rewound on close so audio cannot leak in the background.
   * Gallery viewport videos restart from the beginning when zoom closes.
   * Quick-add / any closed dialog must never restart — audio would leak behind the modal.
   */
  #onDialogClose = () => {
    if (this.#isInsideClosedDialog()) {
      this.#resetAndPause();
      return;
    }

    const inZoom = Boolean(this.closest('zoom-dialog'));
    const zoomOpen = Boolean(document.querySelector('zoom-dialog dialog[open]'));

    if (inZoom) {
      if (!zoomOpen) {
        this.#resetAndPause();
      }
      return;
    }

    if (this.hasAttribute('data-viewport-autoplay')) {
      if (zoomOpen) {
        this.#resetAndPause();
      } else {
        this.#resetVideoToStart();
        requestAnimationFrame(() => this.#syncViewportPlayback());
      }
      return;
    }

    this.#resetAndPause();
  };

  /**
   * Pause and rewind HTML5 video to the start.
   */
  #resetAndPause() {
    this.pauseMedia();
    this.#resetVideoToStart();
  }

  /**
   * Rewind the loaded HTML5 video to t=0 without changing play state beyond seek.
   */
  #resetVideoToStart() {
    const video = this.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;
    try {
      video.currentTime = 0;
    } catch {
      // Ignore seek errors
    }
  }

  /**
   * Whether quick-add desktop is using the stacked scroll gallery.
   * @returns {boolean}
   */
  #isQuickAddDesktopStack() {
    return Boolean(this.closest('.quick-add-modal') && window.matchMedia('(min-width: 750px)').matches);
  }

  /**
   * Whether this media sits on the active slideshow slide (or is not in a slideshow).
   * @returns {boolean}
   */
  #isActiveInSlideshow() {
    const slide = this.closest('slideshow-slide');
    if (!slide) return true;

    // Quick-add desktop stacks every slide in a scroll column — current index stays on 0.
    // Visibility is handled by IntersectionObserver instead of slideshow current.
    if (this.#isQuickAddDesktopStack()) {
      return true;
    }

    const slideshow = /** @type {HTMLElement & { current?: number, slides?: Element[] | NodeListOf<Element> }} */ (
      this.closest('slideshow-component')
    );

    // Prefer the slideshow's current index so peeking adjacent slides don't keep playing.
    if (slideshow?.slides && typeof slideshow.current === 'number') {
      const slides = Array.from(slideshow.slides);
      return slides[slideshow.current] === slide;
    }

    return slide.getAttribute('aria-hidden') !== 'true';
  }

  /**
   * Scroll root for intersection checks.
   * @returns {Element | null}
   */
  #getIntersectionRoot() {
    // Quick-add desktop scrolls inside the sticky media column, not slideshow-slides.
    if (this.#isQuickAddDesktopStack()) {
      return this.closest('.product-information__media');
    }

    const slideshow = this.closest('slideshow-component');
    return (
      /** @type {Element | null} */ (slideshow?.querySelector('slideshow-slides[ref="scroller"], slideshow-slides')) ||
      null
    );
  }

  /**
   * Track native control play/pause so autoplay doesn't override the user.
   * @param {HTMLVideoElement} video
   */
  #bindVideoEvents(video) {
    this.#videoEventsController?.abort();
    this.#videoEventsController = new AbortController();
    const signal = this.#videoEventsController.signal;

    // Keep native control taps from bubbling to gallery zoom handlers
    const stopZoomBubble = (event) => {
      event.stopPropagation();
    };
    video.addEventListener('click', stopZoomBubble, { signal });
    video.addEventListener('pointerdown', stopZoomBubble, { signal });
    video.addEventListener('touchstart', stopZoomBubble, { signal, passive: true });

    video.addEventListener(
      'pause',
      () => {
        this.isPlaying = false;
        if (!this.#pausingProgrammatically) {
          this.#userPaused = true;
        }
        if (this.getAttribute('data-media-loaded')) {
          this.updatePlayPauseHint(false);
        }
      },
      { signal }
    );

    video.addEventListener(
      'play',
      () => {
        this.isPlaying = true;
        this.#userPaused = false;
        this.#hidePreview();
        this.updatePlayPauseHint(true);
      },
      { signal }
    );
  }

  /**
   * Play a video with muted fallback when unmuted autoplay is blocked.
   * @param {HTMLVideoElement} video
   * @returns {Promise<boolean>}
   */
  async #playVideoWithFallback(video) {
    this.#bindVideoEvents(video);
    video.setAttribute('playsinline', '');
    try {
      video.muted = false;
      video.removeAttribute('muted');
      await video.play();
      if (this.#isInsideClosedDialog()) {
        this.#resetAndPause();
        return false;
      }
      return true;
    } catch {
      try {
        video.muted = true;
        await video.play();
        if (this.#isInsideClosedDialog()) {
          this.#resetAndPause();
          return false;
        }
        this.#armGestureRetry();
        return true;
      } catch {
        this.#armGestureRetry();
        return false;
      }
    }
  }

  /**
   * Sync play/pause with slideshow slide changes.
   * @param {Event} event
   */
  #onSlideshowSelect = (event) => {
    const detail = /** @type {CustomEvent & { detail?: { slide?: Element } }} */ (event).detail;
    const selectedSlide = detail?.slide;

    if (selectedSlide) {
      if (selectedSlide.contains(this)) {
        this.#isIntersecting = true;
        this.#playFromViewport();
      } else {
        this.pauseMedia();
      }
      return;
    }

    this.#syncViewportPlayback();
  };

  /**
   * Play only when visible AND on the active slide; otherwise pause immediately.
   */
  #syncViewportPlayback() {
    if (this.#isInsideClosedDialog()) {
      this.pauseMedia();
      return;
    }

    if (this.#isIntersecting && this.#isActiveInSlideshow()) {
      if (this.#userPaused) return;
      this.#playFromViewport();
    } else {
      this.pauseMedia();
    }
  }

  /**
   * Play/pause based on whether the media is in the viewport.
   * @param {AbortSignal} signal
   */
  #setupViewportAutoplay(signal) {
    const slideshow = this.closest('slideshow-component');
    if (slideshow) {
      slideshow.addEventListener(SlideshowSelectEvent.eventName, this.#onSlideshowSelect, { signal });
    }

    const root = this.#getIntersectionRoot();

    this.#viewportObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.#isIntersecting = entry.isIntersecting && entry.intersectionRatio >= 0.5;
          this.#syncViewportPlayback();
        }
      },
      { root, threshold: [0, 0.5, 0.75, 1] }
    );

    this.#viewportObserver.observe(this);
    signal.addEventListener('abort', () => this.#viewportObserver?.disconnect(), { once: true });

    // Initial state (e.g. video is first slide).
    // Quick-add desktop stacks slides in a scroll column — wait for IntersectionObserver.
    if (this.#isQuickAddDesktopStack()) {
      this.#isIntersecting = false;
      return;
    }

    this.#isIntersecting = this.#isActiveInSlideshow();
    this.#syncViewportPlayback();
  }

  /**
   * Retries unmuted play after the first user gesture (browsers often require it).
   * Does not restart a video the user already paused.
   */
  #armGestureRetry() {
    if (this.#gestureRetryArmed) return;
    this.#gestureRetryArmed = true;

    const retry = () => {
      this.#gestureRetryArmed = false;
      if (this.#userPaused || this.#isInsideClosedDialog()) return;
      if (!this.#isIntersecting || !this.#isActiveInSlideshow()) return;

      const video = this.querySelector('video');
      if (video instanceof HTMLVideoElement && !video.paused) {
        // Already playing muted — only try to unmute on gesture.
        video.muted = false;
        video.removeAttribute('muted');
        return;
      }

      this.#playFromViewport();
    };

    document.addEventListener('pointerdown', retry, { once: true, passive: true });
    document.addEventListener('keydown', retry, { once: true });
    document.addEventListener('touchstart', retry, { once: true, passive: true });
  }

  /**
   * Show preview again when playback stops / fails.
   */
  #revealPreview() {
    this.refs.deferredMediaPlayButton?.classList.remove('deferred-media__playing');
  }

  /**
   * Hide preview once the video is actually playing.
   */
  #hidePreview() {
    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
  }

  /**
   * Attempts unmuted playback when the media enters the viewport.
   * Always starts from the beginning when (re)entering play.
   */
  async #playFromViewport() {
    if (this.#userPaused || this.#isInsideClosedDialog()) return;

    if (!this.#isActiveInSlideshow()) {
      this.pauseMedia();
      return;
    }

    if (!this.getAttribute('data-media-loaded') && this.querySelector('template') && !this.querySelector('video, iframe')) {
      this.loadContent(false);
    }

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    /** @type {HTMLVideoElement | null} */
    const video = this.querySelector('video');
    if (video) {
      if (!video.paused && this.isPlaying) return;

      this.#resetVideoToStart();

      const played = await this.#playVideoWithFallback(video);
      if (this.#isInsideClosedDialog()) {
        this.#resetAndPause();
        return;
      }
      this.isPlaying = played;
      if (played) {
        this.#hidePreview();
        this.updatePlayPauseHint(true);
      } else {
        this.#revealPreview();
      }
      return;
    }

    if (this.#isInsideClosedDialog()) {
      this.#resetAndPause();
      return;
    }

    this.playMedia();
    this.#hidePreview();
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Shows the deferred media content
   * @param {Event} [event]
   */
  showDeferredMedia = (event) => {
    event?.stopPropagation?.();
    this.#userPaused = false;
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) {
      const existingVideo = this.querySelector('video');
      if (existingVideo instanceof HTMLVideoElement && !this.#userPaused) {
        void this.#playVideoWithFallback(existingVideo).then((played) => {
          this.isPlaying = played;
          if (played) {
            this.#hidePreview();
            this.updatePlayPauseHint(true);
          }
        });
      }
      return;
    }

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement) {
      this.#bindVideoEvents(content);
    }

    if (content instanceof HTMLVideoElement && content.getAttribute('autoplay') && !this.#userPaused) {
      void this.#playVideoWithFallback(content).then((played) => {
        this.isPlaying = played;
        if (played) {
          this.#hidePreview();
          this.updatePlayPauseHint(true);
        } else {
          this.#revealPreview();
        }
      });
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.#userPaused = true;
      this.pauseMedia();
    } else {
      this.#userPaused = false;
      this.playMedia();
    }
  }

  playMedia() {
    this.#userPaused = false;

    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      /** @type {HTMLVideoElement | null} */
      const video = this.querySelector('video');
      if (video) {
        void this.#playVideoWithFallback(video).then((played) => {
          this.isPlaying = played;
          if (played) {
            this.#hidePreview();
            this.updatePlayPauseHint(true);
          } else {
            this.#revealPreview();
          }
        });
        return;
      }
    }
    this.isPlaying = true;
    this.#hidePreview();
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    this.#pausingProgrammatically = true;

    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // Keep preview hidden if video is already loaded — otherwise the poster shows
    // behind the paused frame after zoom close / slide change.
    // Preview is only restored when playback never started (see #playFromViewport catch).

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }

    queueMicrotask(() => {
      this.#pausingProgrammatically = false;
    });
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
